import { useMutation } from '@tanstack/react-query';
import { findIndex } from 'lodash';
import { produce } from 'immer';
import { handleMutationSuccess, updateGuideVersion } from '../helpers';
import {
  addChapterToGuide,
  deleteChapterFromGuide,
  editChapterTitle,
  moveChapter,
} from '../../helpers/api/guidelinesApi';
import { GUIDE_KEYS } from '../../queries/guides';
import { Chapter, Guide } from '../../types';
import { useCurrentUserIdentity } from '../../queries/identity';
import { queryClient } from '../../constants/app';
import { useGuideStore } from '../../stores/guideStore';

export const useChapterMutations = () => {
  const { data: currentUser } = useCurrentUserIdentity();
  const activeGuideId = useGuideStore(s => s.activeGuideId) as string;
  const activeGuideVersion = useGuideStore(s => s.activeGuideVersion) as number;

  return {
    addChapter: useMutation(
      (options: { newChapterId: string; chapterTitle: string }) =>
        addChapterToGuide(
          activeGuideId,
          options.newChapterId,
          options.chapterTitle,
          activeGuideVersion,
        ),
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyAddChapter(
            activeGuideId,
            variables.newChapterId,
            variables.chapterTitle,
            currentUser?.id ?? '',
          );
        },
        onSuccess: data => {
          handleMutationSuccess('Chapter created', data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    moveChapter: useMutation(
      (options: {
        chapterId: string;
        fromPosition: number;
        toPosition: number;
      }) =>
        moveChapter(
          activeGuideId,
          options.chapterId,
          { position: options.fromPosition },
          { position: options.toPosition },
          activeGuideVersion,
        ),
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyMoveChapter(
            activeGuideId,
            variables.fromPosition,
            variables.toPosition,
          );
        },
        onSuccess: data => {
          handleMutationSuccess('Chapter moved', data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    renameChapter: useMutation(
      (options: { chapterId: string; newChapterTitle: string }) =>
        editChapterTitle(
          activeGuideId,
          options.chapterId,
          options.newChapterTitle,
          activeGuideVersion,
        ),
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyRenameChapter(
            activeGuideId,
            variables.chapterId,
            variables.newChapterTitle,
          );
        },
        onSuccess: data => {
          handleMutationSuccess('Chapter renamed', data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    deleteChapter: useMutation(
      (options: { chapterId: string }) =>
        deleteChapterFromGuide(
          activeGuideId,
          options.chapterId,
          activeGuideVersion,
        ),
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyDeleteChapter(activeGuideId, variables.chapterId);
        },
        onSuccess: data => {
          handleMutationSuccess('Chapter deleted', data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
  };
};

const optimisticallyAddChapter = (
  guideId: string,
  newChapterId: string,
  newChapterTitle: string,
  currentUserId: string,
) => {
  queryClient.setQueryData([GUIDE_KEYS.GUIDE_BY_ID, guideId], (old: Guide) => {
    if (old) {
      const newChapter: Chapter = {
        id: newChapterId,
        shared: false,
        title: newChapterTitle,
        pages: [],
        canEdit: true,
        canView: true,
        ownerId: currentUserId,
      };

      return produce(old, draft => {
        draft.chapters.push(newChapter);
      });
    }

    return old;
  });
};
const optimisticallyDeleteChapter = (guideId: string, chapterId: string) => {
  queryClient.setQueryData([GUIDE_KEYS.GUIDE_BY_ID, guideId], (old: Guide) => {
    if (old) {
      const chapterIndex = findIndex(
        old.chapters,
        chapter => chapter.id === chapterId,
      );

      return produce(old, draft => {
        draft.chapters.splice(chapterIndex, 1);
      });
    }

    return old;
  });
};

const optimisticallyMoveChapter = (
  guideId: string,
  fromPosition: number,
  toPosition: number,
) => {
  queryClient.setQueryData([GUIDE_KEYS.GUIDE_BY_ID, guideId], (old: Guide) => {
    if (old) {
      const chapter = { ...old.chapters[fromPosition] };

      return produce(old, draft => {
        draft.chapters.splice(fromPosition, 1);
        draft.chapters.splice(toPosition, 0, chapter);
      });
    }

    return old;
  });
};
const optimisticallyRenameChapter = (
  guideId: string,
  chapterId: string,
  newChapterTitle: string,
) => {
  queryClient.setQueryData([GUIDE_KEYS.GUIDE_BY_ID, guideId], (old: Guide) => {
    if (old) {
      const chapterIndex = findIndex(
        old.chapters,
        chapter => chapter.id === chapterId,
      );

      return produce(old, draft => {
        draft.chapters[chapterIndex].title = newChapterTitle;
      });
    }

    return old;
  });
};
